import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Chip,
  Tooltip,
  Drawer,
  Typography,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import api from "../../../services/api";

const SpecialtyCakesTable = ({ cakes, onEdit, onDelete }) => {
  const [attributes, setAttributes] = useState([]);
  const [selectedCake, setSelectedCake] = useState(null);

  useEffect(() => {
    const fetchAttributes = async () => {
      try {
        const response = await api.get("/cake-attributes");
        setAttributes(response.data);
      } catch (error) {
        console.error("Failed to fetch attributes:", error);
      }
    };

    fetchAttributes();
  }, []);

  const extractAttributesByType = (attributeIds, type) => {
    return attributeIds
      .map((id) =>
        attributes.find((attr) => attr._id === id && attr.type === type)
      )
      .filter(Boolean)
      .map((attr) => attr.name);
  };

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      <TableContainer
        component={Paper}
        sx={{ border: "1px solid #ddd", borderRadius: "8px" }}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Base Price</TableCell>
              <TableCell>Event Type</TableCell>
              <TableCell>Audience</TableCell>
              <TableCell>Image</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cakes.map((cake) => (
              <TableRow key={cake._id} hover>
                <TableCell>{cake.displayName}</TableCell>
                <TableCell
                  sx={{
                    maxWidth: 400,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}>
                  {cake.description}
                </TableCell>
                <TableCell>${cake.basePrice}</TableCell>
                <TableCell sx={{ maxWidth: 300 }}>
                  {cake.eventType?.map((event) => (
                    <Chip
                      key={event}
                      label={event}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </TableCell>

                <TableCell>
                  {cake.audience?.map((aud) => (
                    <Chip
                      key={aud}
                      label={aud}
                      size="small"
                      sx={{ mr: 0.5, mb: 0.5 }}
                    />
                  ))}
                </TableCell>
                <TableCell>
                  {cake.images?.primary ? (
                    <img
                      src={cake.images.primary}
                      alt={cake.name}
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: "cover",
                        borderRadius: 8,
                      }}
                    />
                  ) : (
                    "-"
                  )}
                </TableCell>

                <TableCell align="center">
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}>
                    <IconButton
                      size="small"
                      sx={{ color: "grey.600" }}
                      onClick={() => setSelectedCake(cake)}>
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ color: "grey.600" }}
                      onClick={() => onEdit(cake)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      sx={{ color: "grey.600" }}
                      onClick={() => onDelete(cake._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Right-side Info Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(selectedCake)}
        onClose={() => setSelectedCake(null)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 400 }, p: 3 } }}>
        {selectedCake && (
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}>
              <Typography variant="h5" fontWeight="bold">
                {selectedCake.displayName}
              </Typography>
              <IconButton
                onClick={() => setSelectedCake(null)}
                size="small"
                sx={{ color: "grey.600", "&:hover": { color: "grey.800" } }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="body2" color="text.secondary" gutterBottom>
              {selectedCake.description}
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
              Base Price: ${selectedCake.basePrice}
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
              Event Types:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selectedCake.eventType?.map((event) => (
                <Chip key={event} label={event} size="small" />
              ))}
            </Box>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
              Audience:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selectedCake.audience?.map((aud) => (
                <Chip key={aud} label={aud} size="small" />
              ))}
            </Box>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
              Decor:
            </Typography>
            <Typography variant="body2">
              {extractAttributesByType(selectedCake.attributes, "decor").join(
                ", "
              ) || "N/A"}
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
              Design Pattern:
            </Typography>
            <Typography variant="body2">
              {extractAttributesByType(
                selectedCake.attributes,
                "designPattern"
              ).join(", ") || "N/A"}
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
              Texture:
            </Typography>
            <Typography variant="body2">
              {extractAttributesByType(selectedCake.attributes, "texture").join(
                ", "
              ) || "N/A"}
            </Typography>

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2 }}>
              Searchable Description:
            </Typography>
            <Typography variant="body2">
              {selectedCake.searchableDescription || "N/A"}
            </Typography>

            {selectedCake.images?.primary && (
              <Box sx={{ mt: 3, mb: 10 }}>
                <img
                  src={selectedCake.images.primary}
                  alt={selectedCake.name}
                  style={{
                    width: "200px",
                    height: "200px",
                    borderRadius: "8px",
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default SpecialtyCakesTable;
